import { ref } from 'vue'
import { defineStore } from 'pinia'
import { searchApi } from '../api/search.api'
import { useUserStore } from '@/modules/users/stores/user.store'
import type { ClientHit, SearchItem } from '../models/search.model'

export const useSearchStore = defineStore('search', () => {
  const userStore = useUserStore()

  const searchQuery = ref<string>('')
  const recentTerms = ref<string[]>([])

  const addRecentTerm = (search: string): void => {
    if (recentTerms.value.includes(search)) return

    recentTerms.value.unshift(search)

    if (recentTerms.value.length > 5) {
      recentTerms.value.pop()
    }
  }

  const removeRecentTerm = (search: string): void => {
    const index = recentTerms.value.indexOf(search)

    if (index !== -1) recentTerms.value.splice(index, 1)
  }

  const mapClient = (
    items: SearchItem[],
    clienKey: string,
    clientName: string,
    returnClientName?: string,
    returnClientKey?: string,
  ): ClientHit => {
    return {
      items: items,
      clientKey: clienKey,
      clientName: clientName,
      returnClientName: returnClientName,
      returnClientKey: returnClientKey,
    }
  }

  const getSearchClients = (result: Record<string, SearchItem[]>) => {
    return Object.entries(result).flatMap<ClientHit>(([key, value]) => {
      const client = userStore.user?.clients.find((client) => client.clientKey === key)
      if (client) {
        return mapClient(value, client.clientKey, client.clientName)
      } else {
        const returnClient = userStore.user?.clients.find(
          (client) => client.returnClientKey === key,
        )

        if (returnClient?.returnClientKey && returnClient?.returnClientName) {
          return mapClient(
            value,
            returnClient.clientKey,
            returnClient.clientName,
            returnClient.returnClientName,
            returnClient.returnClientKey,
          )
        }
      }
      return []
    })
  }

  const getSearchResult = async (): Promise<ClientHit[]> => {
    searchQuery.value = searchQuery.value.trim()

    const { data } = await searchApi.getSearchResult({ query: searchQuery.value })

    if (data && data.query === searchQuery.value) {
      searchQuery.value = data.query

      return getSearchClients(data.result)
    }

    return []
  }

  return {
    searchQuery,
    recentTerms,
    addRecentTerm,
    removeRecentTerm,
    getSearchResult,
  }
})
